import * as React from 'react';

import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody.tsx';
import { PERMISSION } from 'src/features/auth';
import { useGetParkingCards } from 'src/features/cards/api/useParkingsCardsRetrieve.ts';
import useCalculateStayTime from 'src/features/cards/hooks/useCalculateStayTime.ts';
import CancelReservationModal from 'src/features/reservations/components/modals/CancelReservationModal.tsx';
import EditReservationModal from 'src/features/reservations/components/modals/EditReservationModal.tsx';
import RefundPaymentModal from 'src/features/reservations/components/modals/RefundPaymentModal.tsx';
import SendConfirmationModal from 'src/features/reservations/components/modals/SendConfirmationModal.tsx';
import {
    PAYMENT_ORDER_STATUS,
    RESERVATION_STATUS,
    RESERVATION_TYPE,
    type ReservationPaymentOrder,
    type ReservationsVehicleStayList,
} from 'src/features/reservations/types/reservationType.ts';
import { reservationMessages } from 'src/features/reservations/utils/messages.ts';
import { ViewDigitalReceiptModal } from 'src/features/statistics/components/ViewDigitalReceiptModal.tsx';
import { useGetOnlinePaymentReceipt } from 'src/features/statistics/hooks/useGetOnlinePaymentReceipt.ts';
import { statisticsMessages } from 'src/features/statistics/utils';
import { downloadReceipt, generateCanvas } from 'src/features/statistics/utils/digitalReceipt.ts';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings.ts';
import useHasPermissions from 'src/hooks/useHasPermissions.ts';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import { Badge } from 'src/registry/ui/Badge.tsx';
import { Button } from 'src/registry/ui/Button.tsx';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog.tsx';
import ImageModal from 'src/registry/ui/ImageModal';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime.tsx';
import { generalMessages } from 'src/utils/messages.ts';

import type { ReactElement } from 'react';

type ReservationDetailsSidePanelProps = {
    reservation?: ReservationsVehicleStayList;
    reservationPaymentOrder?: ReservationPaymentOrder;
    isMobile?: boolean;
    refetch: () => void;
    refetchPaymentOrders: () => void;
};

type ReservationTitleData = {
    title: string;
    value: string | null | undefined | ReactElement;
    control: any;
};

const ReservationDetailsSidePanel = ({
    reservation,
    isMobile = false,
    reservationPaymentOrder,
    refetch,
    refetchPaymentOrders,
}: ReservationDetailsSidePanelProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const { calculateStayTime } = useCalculateStayTime();
    const { data: parkingZone } = useListSubParkings({ parkingId });

    const { data: vehicleInParkingData } = useGetParkingCards({
        parkingId: parkingId,
        id: reservation?.license_plate ?? '',
        config: { enabled: !!reservation?.license_plate },
    });

    const { data: digitalReceipt } = useGetOnlinePaymentReceipt({
        parkingId,
        paymentOrderNumber: reservationPaymentOrder?.id ?? '',
        config: { enabled: !!reservationPaymentOrder?.id },
    });

    const [stayTime, setStayTime] = React.useState('');
    const [isLoadedFont, setIsLoadedFont] = React.useState(false);

    const permissionReservationsManage = useHasPermissions([PERMISSION.RESERVATIONS_MANAGE]);
    const permissionReservationsManageAdmin = useHasPermissions([PERMISSION.RESERVATIONS_MANAGE_ADMIN]);

    React.useEffect(() => {
        const stayString = calculateStayTime(vehicleInParkingData);
        setStayTime(stayString);

        const interval = setInterval(() => {
            const stayString = calculateStayTime(vehicleInParkingData);
            setStayTime(stayString);
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [calculateStayTime, vehicleInParkingData]);

    React.useEffect(() => {
        const ibmPlexMono = new FontFace(
            'IBM Plex Mono',
            'url(https://fonts.gstatic.com/s/ibmplexmono/v19/-F63fjptAgt5VM-kVkqdyU8n1isq131nj-otFQ.woff2)',
        );
        void ibmPlexMono.load().then(() => {
            document.fonts.add(ibmPlexMono);
            setIsLoadedFont(true);
        });
    }, []);

    const parkingZoneNames = parkingZone?.results.map((zone) => zone.name)?.join(', ');
    const vehicleIsInParking = vehicleInParkingData?.in_parking;

    const disableCancelReservationButton = () => {
        if (!reservation || !reservationPaymentOrder) return true;

        const { status: reservationStatus } = reservation;
        const { status: paymentStatus } = reservationPaymentOrder;

        const isPaid = paymentStatus === PAYMENT_ORDER_STATUS.COMPLETED || PAYMENT_ORDER_STATUS.REFUNDED;
        const isHourlyReservation =
            reservation.vehicle_stay_reservation_type.reservation_type === RESERVATION_TYPE.HOURLY;

        const isUpcomingOrActive =
            reservationStatus === RESERVATION_STATUS.UPCOMING || reservationStatus === RESERVATION_STATUS.ACTIVE;

        return !(isPaid && isUpcomingOrActive && !vehicleIsInParking && !isHourlyReservation);
    };

    const vehicleStatusBadge = () => {
        const status = vehicleInParkingData?.in_parking;
        const color = status ? 'emerald' : 'red';
        const name = status
            ? formatMessage(reservationMessages.inParking)
            : formatMessage(reservationMessages.notInParking);
        return (
            <Badge theme={color}>
                <FontAwesomeIcon icon={status ? faCheckCircle : faXmarkCircle} />
                <div>{name}</div>
            </Badge>
        );
    };

    const reservationStatus = reservation && reservation.status;
    const vehicleIsNotInParking = reservationStatus !== RESERVATION_STATUS.UPCOMING && vehicleInParkingData?.in_parking;

    const sidePanelBodyAttributeMapping: ReservationTitleData[] = [
        {
            title: formatMessage(reservationMessages.vehicleStatus),
            value: vehicleStatusBadge(),
            control: parkingId,
        },
        {
            title: formatMessage(reservationMessages.parkingZones),
            value: vehicleIsNotInParking ? parkingZoneNames : '-',
            control: parkingZoneNames,
        },
        {
            title: formatMessage(reservationMessages.enteredAt),
            value: vehicleIsNotInParking
                ? getFormattedDateTimeForDetailPanel(vehicleInParkingData?.enter_datetime) || '-'
                : '-',
            control: vehicleInParkingData,
        },
        {
            title: formatMessage(reservationMessages.exited),
            value: vehicleIsNotInParking
                ? getFormattedDateTimeForDetailPanel(vehicleInParkingData?.exit_time.toString()) || '-'
                : '-',
            control: vehicleInParkingData,
        },
        {
            title: formatMessage(reservationMessages.totalStay),
            value: vehicleIsNotInParking ? stayTime : '-',
            control: vehicleInParkingData,
        },
    ];

    const receiptTextLines = digitalReceipt?.details?.trimEnd().split('\n');
    const fileName = `${formatMessage(generalMessages.smartPark)} ${formatMessage(statisticsMessages.digitalReceipt)}`;
    const handleDownload = () => {
        const canvas = generateCanvas(receiptTextLines, isLoadedFont);
        downloadReceipt(canvas, fileName);
    };

    const disableButtons =
        reservationPaymentOrder?.status !== PAYMENT_ORDER_STATUS.REFUNDED &&
        reservationPaymentOrder?.status !== PAYMENT_ORDER_STATUS.COMPLETED;

    return (
        <>
            <DialogHeader>
                <div className="flex items-center gap-2">
                    <DialogTitle className="pr-0 font-mono">
                        {`${formatMessage(reservationMessages.reservation)} #${reservation?.number}`}
                    </DialogTitle>
                </div>
                <DialogDescription>{reservation?.license_plate}</DialogDescription>
            </DialogHeader>
            <DialogSeparator />
            <DialogBody>
                <ImageModal
                    image={`/parkings/images/${parkingId}/${vehicleInParkingData?.photo_path}`}
                    alt={`${formatMessage(generalMessages.vehicle)} ${reservation?.license_plate}`}
                    title={reservation?.license_plate ?? vehicleInParkingData?.card_number}
                    disableClick={!vehicleInParkingData?.photo_path}
                />
                {permissionReservationsManage && (
                    <div className="grid gap-2">
                        <EditReservationModal
                            selectedReservation={reservation}
                            inParking={vehicleInParkingData?.in_parking}
                            refetch={refetch}
                        >
                            <DialogTrigger asChild>
                                <Button variant="default" className="w-full">
                                    <FormattedMessage {...reservationMessages.edit} />
                                </Button>
                            </DialogTrigger>
                        </EditReservationModal>
                        <div className="grid grid-cols-2 gap-2">
                            <ViewDigitalReceiptModal
                                downloadReceipt={handleDownload}
                                digitalReceiptDetails={digitalReceipt?.details}
                            >
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="w-full" disabled={disableButtons}>
                                        <FormattedMessage {...reservationMessages.viewReceipt} />
                                    </Button>
                                </DialogTrigger>
                            </ViewDigitalReceiptModal>

                            <SendConfirmationModal selectedReservation={reservation}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="w-full" disabled={disableButtons}>
                                        <FormattedMessage {...reservationMessages.sendConfirmation} />
                                    </Button>
                                </DialogTrigger>
                            </SendConfirmationModal>
                        </div>

                        {permissionReservationsManageAdmin && (
                            <div className="grid grid-cols-2 gap-2">
                                <RefundPaymentModal
                                    reservationNumber={reservation?.number || ''}
                                    reservationId={reservation?.id || ''}
                                    refetch={refetchPaymentOrders}
                                >
                                    <DialogTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                                            disabled={
                                                reservationPaymentOrder?.status === PAYMENT_ORDER_STATUS.REFUNDED ||
                                                reservationPaymentOrder?.status !== PAYMENT_ORDER_STATUS.COMPLETED
                                            }
                                        >
                                            <FormattedMessage {...reservationMessages.refundPayment} />
                                        </Button>
                                    </DialogTrigger>
                                </RefundPaymentModal>

                                <CancelReservationModal selectedReservation={reservation} refetch={refetch}>
                                    <DialogTrigger asChild>
                                        <Button
                                            variant="destructiveLight"
                                            className="w-full"
                                            disabled={disableCancelReservationButton()}
                                        >
                                            <FormattedMessage {...reservationMessages.cancelReservation} />
                                        </Button>
                                    </DialogTrigger>
                                </CancelReservationModal>
                            </div>
                        )}
                    </div>
                )}
                <div className="mt-4 grid gap-2">
                    {sidePanelBodyAttributeMapping.map((details: ReservationTitleData) => {
                        return (
                            <SidePanelBodyAttribute key={details.title}>
                                <SidePanelBodyAttributeTitle>{details.title}</SidePanelBodyAttributeTitle>
                                <SidePanelBodyAttributeBody control={details.control}>
                                    {details.value ?? '-'}
                                </SidePanelBodyAttributeBody>
                            </SidePanelBodyAttribute>
                        );
                    })}
                </div>
            </DialogBody>
            {isMobile && (
                <>
                    <DialogSeparator />
                    <DialogFooter className="flex flex-col gap-2 sm:flex-col">
                        <DialogClose asChild>
                            <Button variant="outline" className="flex-1">
                                <FormattedMessage {...generalMessages.close} />
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </>
            )}
        </>
    );
};

export default ReservationDetailsSidePanel;
